import { getDevTokenHint } from '../../../apps/apis/claudeCodeDashboard/devToken';
import type { CommandCenterSummaryView, RunStatus, RunSummaryView } from '../../../apps/apis/commandCenter/types';
import type { AgentCard, AgentStatus, DetailMeta, DetailRow, DetailRunResult } from '../types';
import { CARD_RUNS_LABEL } from './catalog';

/** KPI tile values sourced from GET /agent/commandCenter/summary. */
export interface LiveKpis {
  nodesOnline: number;
  nodesTotal: number;
  runsToday: number;
  /** Percent 0–100 with the board's one-decimal rounding; the API reports a 0–1 ratio. */
  successRate: number;
}

const RESULT_BY_STATUS: Record<RunStatus, DetailRunResult> = {
  ok: 'ok',
  failed: 'fail',
  orphaned: 'warn',
  needs_review: 'warn',
  running: 'idle',
};

const num = (value: number | undefined): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/** The API's succeeded/terminal ratio as a percent, rounded like the board's success cell. */
const percentOf = (ratio: number | undefined): number => Math.round(num(ratio) * 1000) / 10;

/** Reads the KPI tiles off the summary, tolerating a payload that has not loaded or is partial. */
export const kpisFromSummary = (summary: CommandCenterSummaryView | undefined): LiveKpis => ({
  nodesOnline: num(summary?.nodes?.online),
  nodesTotal: num(summary?.nodes?.total),
  runsToday: num(summary?.runsToday?.total),
  successRate: percentOf(summary?.runsToday?.successRate),
});

/** Start of the current UTC day — the same day boundary the summary API counts native runs against. */
const startOfUtcDay = (now: Date): number => Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

/**
 * Folds today's live n8n executions into the KPI tiles. n8n runs live outside cc_runs, so the summary
 * API never counts them; without this the KPI bar reads 0 even while n8n workflows run all day. Native
 * and n8n counts are combined so the success rate reflects the whole board, not just node runs.
 */
export const kpisWithN8n = (
  summary: CommandCenterSummaryView | undefined,
  n8nRuns: RunSummaryView[],
  now: Date = new Date(),
): LiveKpis => {
  const base = kpisFromSummary(summary);
  const dayStart = startOfUtcDay(now);
  const today = n8nRuns.filter((run) => {
    const t = Date.parse(run.createdAt);
    return Number.isFinite(t) && t >= dayStart;
  });
  if (today.length === 0) {
    return base;
  }

  const n8nOk = today.filter((r) => r.status === 'ok').length;
  const n8nFailed = today.filter((r) => r.status === 'failed').length;

  const nativeSucceeded = num(summary?.runsToday?.succeeded);
  const nativeTerminal = nativeSucceeded + num(summary?.runsToday?.failed) + num(summary?.runsToday?.needsReview);
  const succeeded = nativeSucceeded + n8nOk;
  const terminal = nativeTerminal + n8nOk + n8nFailed;

  return {
    ...base,
    runsToday: base.runsToday + today.length,
    successRate: terminal === 0 ? 0 : Math.round((succeeded / terminal) * 1000) / 10,
  };
};

/**
 * Run counters for the detail header. The board card already carries its week of live counters (folded
 * from the heatmap by `withRunStats`), so the header reuses them and the two views never disagree;
 * the seeded meta is only shown while the card has no live runs.
 */
export const detailRunMeta = (
  card: AgentCard,
  seed: DetailMeta,
): { label: string; runs: string; succ: DetailMeta['succ'] } => {
  if (card.runs === 0) {
    return { label: CARD_RUNS_LABEL, runs: seed.runs, succ: seed.succ };
  }
  const tone: AgentStatus = card.dot === 'ok' || card.dot === 'warn' || card.dot === 'crit' ? card.dot : 'idle';
  // succText is the full board cell ('success 100%'); the header prints its own 'success' label.
  return { label: card.runsLabel, runs: card.runsText, succ: [card.succText.replace(/^success\s+/, ''), tone] };
};

/** Sub-labels under each KPI tile; an empty fleet reads as "nothing connected yet", not as breakage. */
export const summaryFootnotes = (summary: CommandCenterSummaryView | undefined): string[] => {
  if (!summary) {
    return ['—', '—', '—'];
  }

  const nodes = summary.nodes ?? { total: 0, online: 0, offline: 0 };
  const runs = summary.runsToday ?? { total: 0, running: 0, succeeded: 0, failed: 0, needsReview: 0, successRate: 0 };
  const finished = num(runs.succeeded) + num(runs.failed) + num(runs.needsReview);

  return [
    num(nodes.total) === 0 ? 'no nodes connected yet' : `${num(nodes.offline)} offline`,
    num(runs.total) === 0
      ? 'no runs recorded today'
      : `${num(runs.running)} running · ${num(runs.failed)} failed · ${num(runs.needsReview)} needs review`,
    finished === 0 ? 'no finished runs today' : `${num(runs.succeeded)} of ${finished} finished runs succeeded`,
  ];
};

export interface LiveDataStatus {
  /** Whether an internal-api dashboard JWT is present; the admin session cookie is not enough. */
  hasToken: boolean;
  catalogError: boolean;
  heatmapError: boolean;
  summaryError: boolean;
  nodesError: boolean;
  runsError: boolean;
}

export interface LiveDataNotice {
  tone: 'info' | 'warn';
  text: string;
}

/**
 * One banner for the whole live layer. Missing token is an expected state (sign-in), not an error,
 * so it wins over the 401s it causes; otherwise it names exactly which resources are down. The catalog
 * is called out separately because it is the board itself, not something drawn beside it.
 */
export const liveDataNotice = (status: LiveDataStatus): LiveDataNotice | null => {
  if (!status.hasToken) {
    // Locally the entrypoint registers a hint; in a production bundle that call is stripped and the notice stays bare.
    const hint = getDevTokenHint();
    return {
      tone: 'info',
      text: `The Command Center needs a dashboard sign-in — no agent data can be loaded.${hint ? ` ${hint}` : ''}`,
    };
  }
  const down = [
    status.heatmapError ? 'heatmap' : null,
    status.summaryError ? 'summary' : null,
    status.nodesError ? 'nodes' : null,
    status.runsError ? 'runs' : null,
  ].filter((name): name is string => name !== null);

  if (status.catalogError) {
    const also = down.length === 0 ? '' : ` (also down: ${down.join(', ')})`;
    return { tone: 'warn', text: `The agent catalog could not be loaded — no cards to show${also}.` };
  }
  if (down.length === 0) {
    return null;
  }
  return { tone: 'warn', text: `live data unavailable: ${down.join(', ')} — the catalog below is unaffected` };
};

export interface CatalogStatus {
  hasToken: boolean;
  isError: boolean;
  /** Whether a catalog response has ever been folded into the board. */
  hydrated: boolean;
  total: number;
}

/** The chip beside the Agents heading — it must never claim live data the board does not actually have. */
export const catalogLabel = (status: CatalogStatus): string => {
  if (status.hydrated) {
    // Cards are on screen; a failed refetch makes them stale, not absent.
    const cards = status.total === 0 ? 'no cards yet' : `${status.total} cards`;
    return status.isError ? `${cards} · stale` : `live · ${cards}`;
  }
  if (!status.hasToken) {
    return 'sign in to load';
  }
  return status.isError ? 'catalog unavailable' : 'loading…';
};

/** Result column for one run; unknown statuses degrade to idle rather than throwing. */
export const runResultOf = (status: RunStatus): DetailRunResult => RESULT_BY_STATUS[status] ?? 'idle';

/** 'MM-DD HH:mm' in local time; the raw ISO string is kept if it cannot be parsed. */
const startedAt = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** What an absent measurement renders as, so a missing number never reads as a real one. */
const DASH = '—';

/** Sub-second runs read as milliseconds; anything longer reads as seconds, which is how they are compared. */
const elapsed = (durationMs: number | null): string => {
  if (durationMs === null) return DASH;
  return durationMs < 1000 ? `${durationMs}ms` : `${(durationMs / 1000).toFixed(1)}s`;
};

/** A run that predates the metric reports nothing, which is not the same as reporting zero tools. */
const countOf = (toolCallCount: number | null): string => (toolCallCount === null ? DASH : String(toolCallCount));

/**
 * Recent-run rows for one catalog card. The join key is `run.agentId` — the documented catalog card id
 * (a run carries `agentId` = the card it belongs to, or null when its agent is not on the board). We fall
 * back to the free-form `run.agent` label only for legacy runs that predate agent_id, never fuzzier than
 * exact. Returns [] when nothing matches so the caller can fall back to the seed rows.
 */
export const liveRunRows = (runs: RunSummaryView[], agentId: string): DetailRow[] =>
  runs
    .filter((r) => (r.agentId ?? r.agent) === agentId)
    .map((r) => [
      startedAt(r.createdAt),
      runResultOf(r.status),
      r.summary ?? r.status,
      elapsed(r.durationMs),
      countOf(r.toolCallCount),
    ]);
